import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row, Stack } from 'react-bootstrap';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  fetchFailureData,
  removeFailureData,
} from '../../../../modules/executions';
import SkeletonLoader from '../../../elements/SkeletonLoader';
import AddEditFailure from './AddEditFailure';
import { IconButton } from '@mui/material';
import ConfirmationModal from '../../../elements/ConfirmationModal';
import { useNotification } from '../../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../../lib/userUtils';
import { WarningIcon } from '../../../elements/CustomIcons';

const FailuresAnalysis = ({ executionId, testCaseId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [mode, setMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchFailureData(executionId);
      setData(response.failures);
    } catch (error) {
      addNotification('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOpen = (type, data) => {
    setShowAddPopup(true);
    setMode(type);
    setSelectedItem(data);
  };

  const handleClose = () => {
    setShowAddPopup(false);
    setSelectedItem(null);
  };

  const handleOpenDeleteConfirm = (data) => {
    setOpenDeleteConfirm(true);
    setSelectedItem(data);
  };

  const handleCloseConfirm = () => {
    setOpenDeleteConfirm(false);
    setSelectedItem(null);
  };

  const handleDelete = async () => {
    try {
      await removeFailureData(selectedItem?.id);
      fetchData();
      handleCloseConfirm();
      addNotification('success', 'Item removed successfully');
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (executionId) {
      fetchData();
    }
    return () => {
      setData([]);
    };
  }, [executionId]);

  const showWarning = data?.length === 0;

  return (
    <Card className="shadow-sm">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ height: '50px' }}>
        <span className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Failure Analysis</h5>
          {showWarning && (
            <WarningIcon tooltipText="Add a failure analysis to this failed execution." />
          )}
        </span>
        {!isExecutiveManager() && data?.length === 0 && (
          <div>
            <Button
              variant="link"
              onClick={() => handleAddOpen('add', null)}
              title="Add New Analysis">
              <Add />
            </Button>
          </div>
        )}
      </Card.Header>

      <Card.Body>
        <Container fluid>
          {data?.length === 0 && (
            <Row>
              <Col className="text-center">
                {loading ? (
                  <SkeletonLoader rowCount={2} />
                ) : (
                  'No data added yet'
                )}
              </Col>
            </Row>
          )}
          {data.length > 0 && (
            <Stack gap={3}>
              {data.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  <Container fluid className="px-0">
                    <Row>
                      <Col className="d-flex justify-content-end">
                        <IconButton onClick={() => handleAddOpen('edit', item)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenDeleteConfirm(item)}>
                          <Delete sx={{ color: 'red' }} />
                        </IconButton>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <h6 className="text-secondary">Category</h6>
                        <p>{item.category}</p>
                      </Col>
                      <Col>
                        <h6 className="text-secondary">Sub Category</h6>
                        <p>{item.sub_category}</p>
                      </Col>
                      <Col>
                        <h6 className="text-secondary">Added By</h6>
                        <p>{item.added_by}</p>
                      </Col>
                    </Row>
                    <Row className="d-flex justify-content-between">
                      <Col md="10">
                        <h6 className="text-secondary">Description</h6>
                        <p>{item.description || 'No details added'}</p>
                      </Col>
                    </Row>
                  </Container>
                  {index < data.length - 1 && <hr />}
                </React.Fragment>
              ))}
            </Stack>
          )}
        </Container>
      </Card.Body>
      <AddEditFailure
        show={showAddPopup}
        handleClose={handleClose}
        mode={mode}
        fetchData={fetchData}
        selectedItem={selectedItem}
        testCaseId={testCaseId}
        executionId={executionId}
      />
      <ConfirmationModal
        show={openDeleteConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleDelete}
        body="Are you sure you want to remove this item?"
      />
    </Card>
  );
};

export default FailuresAnalysis;
